import React, { useEffect, useState } from 'react';
import { Card, Input, Typography, message, Table, Modal, Spin, Space, Tooltip } from 'antd';
import { FolderOpenOutlined, FileOutlined, DeleteOutlined, DownloadOutlined, FolderAddOutlined, SwapOutlined, EyeOutlined, CopyOutlined } from '@ant-design/icons';
import FileDownloadTool from '../../../components/common/FileDownloadTool';
import FilePreviewModal, { canPreviewFile } from '../../../components/common/FilePreviewModal';
import { useTranslation } from 'react-i18next';
import logger from '../../../utils/logger'; // Change to default import
import { useAuth } from '../../../components/common/security/AuthContext';
import { dccCopy, dccDelete, dccDownloadUrl, dccListStats, type DccListStatsItem, dccMkdir, dccMove } from '../../../services/dccApi';
import Button from '../../../components/common/Button';

const { Title } = Typography;

interface FolderItemRow {
  key: string;
  name: string;
  path: string;
  isFolder: boolean;
  createdAt?: number;
}

const CoreFolderManager: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [currentPath, setCurrentPath] = useState('');
  const [items, setItems] = useState<FolderItemRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [newFolder, setNewFolder] = useState('');
  const [moveModal, setMoveModal] = useState<{visible: boolean, src: string}>({visible: false, src: ''});
  const [moveDst, setMoveDst] = useState('');
  const [copyModal, setCopyModal] = useState<{visible: boolean, src: string}>({visible: false, src: ''});
  const [copyDst, setCopyDst] = useState('');
  const [previewPath, setPreviewPath] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const mapStatsItems = (data: DccListStatsItem[]): FolderItemRow[] => {
    return data.map((item) => {
      const name = String(item.name || '').replace(/\\/g, '/');
      const path = String(item.path || name || '').replace(/\\/g, '/').replace(/^\/+/, '').replace(/\/+$/, '');

      return {
        key: path || name,
        name: name.replace(/\/$/, ''),
        path,
        isFolder: Boolean(item.is_dir),
        createdAt: typeof item.created_at === 'number' ? item.created_at : undefined,
      };
    });
  };

  // Klasör/dosya listeleme
  const fetchList = async (path = '') => {
    setLoading(true);
    const pathToLog = `/api/dcc/list_stats?path=${encodeURIComponent(path)}`;
    logger.debug(`Attempting to fetch file list from path: ${pathToLog}`); // Log the full URL for debugging
    try {
      const data = await dccListStats(path, token);
      if (Array.isArray(data)) {
        const mapped = mapStatsItems(data as DccListStatsItem[]);
        setItems(mapped);
        setCurrentPath(path);
      } else {
        message.error(t('folderManager.listError'));
      }
    } catch (e) {
      message.error(t('folderManager.serverError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(''); }, []);

  // Klasör oluşturma
  const handleCreate = async () => {
    if (!newFolder.trim()) {
      message.warning(t('folderManager.enterFolderName'));
      return;
    }
    setLoading(true);
    try {
      await dccMkdir(currentPath ? `${currentPath}/${newFolder}` : newFolder, token);
        message.success(t('folderManager.folderCreated'));
        setNewFolder('');
        fetchList(currentPath);
    } catch (e) {
      message.error(t('folderManager.serverError'));
    }
    setLoading(false);
  };

  // Silme
  const handleDelete = async (record: FolderItemRow) => {
    const fullPath = record.path;
    Modal.confirm({
      title: t('folderManager.deleteConfirm'),
      content: fullPath,
      okText: t('folderManager.yes'),
      cancelText: t('folderManager.no'),
      onOk: async () => {
        setLoading(true);
        try {
          await dccDelete(fullPath, token);
            message.success(t('folderManager.deleted'));
            fetchList(currentPath);
        } catch (e) {
          const errMsg = String((e as any)?.message || e || t('folderManager.deleteFailed'));
          logger.error('DCC delete failed', { path: fullPath, currentPath, errMsg, error: e });
          // Show a short message in UI; details are in console/logger.
          message.error(errMsg);
        }
        setLoading(false);
      }
    });
  };

  // Taşıma
  const handleMove = async () => {
    setLoading(true);
    try {
      await dccMove(moveModal.src, moveDst, token);
        message.success(t('folderManager.moveSuccess'));
        setMoveModal({visible: false, src: ''});
        setMoveDst('');
        fetchList(currentPath);
    } catch (e) {
      message.error(t('folderManager.serverError'));
    }
    setLoading(false);
  };

  // Kopyalama
  const handleCopy = async () => {
    setLoading(true);
    try {
      await dccCopy(copyModal.src, copyDst, token);
      message.success(t('folderManager.copySuccess'));
      setCopyModal({ visible: false, src: '' });
      setCopyDst('');
      fetchList(currentPath);
    } catch (e) {
      const errMsg = String((e as any)?.message || e || t('folderManager.copyFailed'));
      logger.error('DCC copy failed', { src: copyModal.src, currentPath, dst: copyDst, errMsg, error: e });
      message.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  // İndirme
  const handleDownload = (record: FolderItemRow) => {
    const fullPath = record.path;
    const url = dccDownloadUrl(fullPath, token, false);
    window.open(url, '_blank');
  };

  // Dosyayı modal içinde görüntüleme (PDF + görsel)
  const handlePreview = (record: FolderItemRow) => {
    const fullPath = record.path;
    setPreviewPath(fullPath);
    setIsPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setIsPreviewOpen(false);
    setPreviewPath(null);
  };

  // Klasöre gir
  const handleEnterFolder = (record: FolderItemRow) => {
    fetchList(record.path);
  };

  // Üst klasöre çık
  const handleGoUp = () => {
    if (!currentPath) return;
    const parts = currentPath.split('/');
    parts.pop();
    fetchList(parts.join('/'));
  };

  // Tablo kolonları
  const columns = [
    {
      title: t('folderManager.colName'),
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: any) =>
        record.isFolder ? (
          <span style={{ cursor: 'pointer', color: '#1677ff' }} onClick={() => handleEnterFolder(record)}>
            <FolderOpenOutlined /> {record.name}
          </span>
        ) : (
          <span><FileOutlined /> {record.name}</span>
        )
    },
    {
      title: t('folderManager.colCreated'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (_: any, record: FolderItemRow) => {
        if (!record.createdAt) return '';
        try {
          const d = new Date(record.createdAt * 1000);
          if (Number.isNaN(d.getTime())) return '';
          return d.toLocaleString();
        } catch {
          return '';
        }
      },
    },
    {
      title: t('folderManager.colActions'),
      key: 'actions',
      width: 120,
      render: (_: any, record: any) => (
        <Space>
          <Tooltip title={t('folderManager.move')}>
            <Button action="edit" icon={<SwapOutlined />} size="small" onClick={() => setMoveModal({visible: true, src: record.path})} />
          </Tooltip>
          <Tooltip title={t('folderManager.copy')}>
            <Button action="add" icon={<CopyOutlined />} size="small" onClick={() => setCopyModal({ visible: true, src: record.path })} />
          </Tooltip>
          <Tooltip title={t('folderManager.delete')}>
            <Button action="delete" icon={<DeleteOutlined />} size="small" danger onClick={() => handleDelete(record)} />
          </Tooltip>
          {!record.isFolder && (
            <Tooltip title={t('folderManager.download')}>
              <Button icon={<DownloadOutlined />} size="small" onClick={() => handleDownload(record)} />
            </Tooltip>
          )}
          {!record.isFolder && canPreviewFile(record.name) && (
            <Tooltip title={t('folderManager.preview')}>
              <Button icon={<EyeOutlined />} size="small" onClick={() => handlePreview(record)} />
            </Tooltip>
          )}
          {record.isFolder && (
            <FileDownloadTool
              contentPath={record.path}
              buttonText={
                <Tooltip title={t('folderManager.download')}>
                  <Button icon={<DownloadOutlined />} size="small" />
                </Tooltip>
              }
            />
          )}
        </Space>
      )
    }
  ];

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', minHeight: '70vh' }}>
      <Card style={{ minWidth: 500, boxShadow: '0 2px 8px #f0f1f2', marginTop: 32 }}>
        <Title level={3} style={{ textAlign: 'center' }}>{t('folderManager.title')}</Title>
        <Space style={{ marginBottom: 16 }}>
          <Button onClick={handleGoUp} disabled={!currentPath}>{t('folderManager.parentFolder')}</Button>
          <Input
            placeholder={t('folderManager.newFolderPlaceholder')}
            value={newFolder}
            onChange={e => setNewFolder(e.target.value)}
            onPressEnter={handleCreate}
            disabled={loading}
            style={{ width: 180 }}
          />
          <Button action="add" type="primary" icon={<FolderAddOutlined />} loading={loading} onClick={handleCreate}>{t('folderManager.create')}</Button>
        </Space>
        <Spin spinning={loading} tip={t('folderManager.loading')}>
          <Table
            columns={columns}
            dataSource={items}
            pagination={false}
            locale={{ emptyText: t('folderManager.empty') }}
            size="middle"
            style={{ marginTop: 16 }}
          />
        </Spin>
        <FilePreviewModal
          open={isPreviewOpen}
          path={previewPath}
          token={token}
          onClose={handleClosePreview}
          width="85%"
        />
        <Modal
          open={moveModal.visible}
          title={t('folderManager.moveTitle')}
          onCancel={() => setMoveModal({visible: false, src: ''})}
          onOk={handleMove}
          okText={t('folderManager.move')}
          cancelText={t('folderManager.cancel')}
        >
          <Input
            placeholder={t('folderManager.movePlaceholder')}
            value={moveDst}
            onChange={e => setMoveDst(e.target.value)}
          />
        </Modal>

        <Modal
          open={copyModal.visible}
          title={t('folderManager.copyTitle')}
          onCancel={() => setCopyModal({ visible: false, src: '' })}
          onOk={handleCopy}
          okText={t('folderManager.copy')}
          cancelText={t('folderManager.cancel')}
        >
          <Input
            placeholder={t('folderManager.copyPlaceholder')}
            value={copyDst}
            onChange={(e) => setCopyDst(e.target.value)}
          />
        </Modal>
      </Card>
    </div>
  );
};

export default CoreFolderManager; 
